import { isEmpty } from 'lodash-es';
import { SESSION_QUERY_PASSTHROUGH } from '~/constants/config';
import { PageKey } from '~/constants/page';
import { STEPS } from '~/constants/teamCreate';
import { useHomeNav, useTopNav } from '~/hooks/useNav';

export default defineNuxtRouteMiddleware((to, from) => {
  const nav = useTopNav();
  const router = useRouter();
  const homeMenu = useHomeNav();
  const { isMobile } = useDevice();
  const session = useSession();

  const safeDecode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const getLoginRedirect = () => {
    const rawRedirect = to.query.redirect;
    if (!rawRedirect || typeof rawRedirect !== 'string') return '';

    if (rawRedirect.includes('%25')) {
      return safeDecode(safeDecode(rawRedirect));
    }

    if (rawRedirect.includes('%')) {
      return safeDecode(rawRedirect);
    }

    return rawRedirect;
  };

  // 伪静态的重定向逻辑，用于兜底生产的留存链接
  const isPseudoStatic = router.options.routes.some(item => {
    const isPseudoStaticPagePath = item.path.endsWith('.html');
    if (isPseudoStaticPagePath && !to.path.endsWith('.html')) {
      const itemPrefix = item.path.split('/').slice(0, -1).join('/');
      const toPrefix = to.path.split('/').slice(0, -1).join('/');
      return itemPrefix === toPrefix;
    }
    return false;
  });

  if (isPseudoStatic) {
    return navigateTo(`${to.path}.html`);
  }

  const preservedPassthroughKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'from',
    'discount_code',
    'ref',
    'keyword_search',
    'gclid',
    'msclkid',
    'bd_vid',
    'qhclickid',
  ];

  // url 参数的透传逻辑
  if (import.meta.client) {
    const patch: Record<string, any> = {};
    const update: Record<string, any> = {};
    const pth = sessionGetItem(SESSION_QUERY_PASSTHROUGH) || {};

    const lowerKeyQuery = Object.fromEntries(
      Object.entries(to.query).map(([key, value]) => [key.toLocaleLowerCase(), value]),
    );

    // 如果bd_vid 有值 utm_source=baidusem_ocpc，
    // 如果qhclickid有值 utm_source=qihusem_ocpc
    // 如果msclkid有值 utm_source=bingsem_ocpc
    if (!lowerKeyQuery['utm_source']) {
      const clickIdToUtmSource = {
        bd_vid: 'baidusem_ocpc',
        qhclickid: 'qihusem_ocpc',
        msclkid: 'bingsem_ocpc',
      };

      for (const [clickId, utmSource] of Object.entries(clickIdToUtmSource)) {
        if (lowerKeyQuery[clickId]) {
          update['utm_source'] = utmSource;
          break;
        }
      }
    }

    preservedPassthroughKeys.forEach(key => {
      if (!lowerKeyQuery[key] && pth[key]) {
        patch[key] = pth[key];
      }

      if (lowerKeyQuery[key]) {
        update[key] = lowerKeyQuery[key];
      }
    });

    if (!isEmpty(update)) {
      sessionSetItem(SESSION_QUERY_PASSTHROUGH, { ...pth, ...update });
    }

    if (!isEmpty(patch)) {
      return router.replace({
        path: to.path,
        query: {
          ...to.query,
          ...patch,
        },
      });
    }
  }

  if (to.path === '/scene-details') {
    const lookName = to.query.look_name;
    if (lookName) {
      // 使用 301 永久重定向
      return navigateTo(`/studio/${lookName}.html`, { redirectCode: 301, external: false });
    }
    // 如果没有参数，404处理
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
  }

  if (to.path.endsWith('scenarios') || to.path.endsWith('scenarios/')) {
    const scenariosNav = nav.value.find(item => item.pageKey === PageKey.SCENARIOS);
    const targetRouteConfig = scenariosNav?.children?.[0];

    if (targetRouteConfig) {
      return navigateTo(targetRouteConfig.route);
    }
  }

  // 登录状态打开登录页面重定向
  if (session.value?.user_id) {
    if (to.name === 'login') {
      const redirect = getLoginRedirect();
      if (redirect) {
        return navigateTo(redirect, { external: true });
      }
      return isMobile
        ? navigateTo('/', { external: true })
        : navigateTo('/workspace', { external: true });
    }

    // 团队体验版、入门版创建过程，如果是跳转第一步，则默认跳转第二步
    const step = to.query.step || STEPS.STEP0;
    if (
      [PageKey.TEAM_STARTER_CREATE, PageKey.TEAM_TASTE_CREATE].includes(to.name as PageKey) &&
      Number(step) === STEPS.STEP0
    ) {
      return router.replace({
        name: to.name as string,
        query: { ...to.query, step: STEPS.STEP1 },
      });
    }
  }

  // 首页相关页面
  const homePage = homeMenu.value.find(o => o.route === to.path && to.name !== 'home');

  // 企业权限
  if (!session.value?.user_id && homePage) {
    return navigateTo('/');
  }

  // 模板页的重定向逻辑
  if (/template\/([^/]+)$/.test(to.path) && isMobile) {
    return navigateTo(`/template/m/${to.params.id}.html`);
  }

  if (/template\/m\/([^/]+)$/.test(to.path) && !isMobile) {
    return navigateTo(`/template/${to.params.id}.html`);
  }
});
